/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Grid,
  Radio,
  FormLabel,
  TextField,
  RadioGroup,
  Typography,
  FormControl,
  FormControlLabel,
  useMediaQuery,
} from '@material-ui/core';
import { isEmpty } from 'lodash';
import { useFormik } from 'formik';

import { useAlert } from 'components/Alert/Alert';
import CPFInput from 'components/CPFInput/CPFInput';
import PhoneInput from 'components/PhoneInput/PhoneInput';
import TermButton from 'components/TermButton/TermButton';

import useTerm from 'hooks/useTerm';
import useReset from 'hooks/useReset';
import useParticipant from 'hooks/useParticipant';
import useProposal from 'hooks/useProposal';
import useTrustService from 'hooks/useTrustService';
import useValidator from 'hooks/useValidator';
import {
  MESSAGE_PERSON_ERROR,
  MESSAGE_ABOUT_CHARACTERS,
  MESSAGE_TERMS_ERROR,
  MESSAGE_GET_PERSON_ERROR,
  MESSAGE_GET_PERSON_ERROR_RESTRICT,
} from 'constants/messages';
import {
  MAX_CHARACTERS_ABOUT,
  HEIGHT_STEP_CONTAINER,
  CPF_SIZE,
  RESTRICTDOCUMENTERRORCODE,
  FETCH_STATUS,
} from 'constants/defaults';
import { ABOUT_YOU } from 'constants/textsSteps';
import ButtonsControl from '../shared/ButtonsControl';
import TermPolicy from './TermPolicy';
import TermService from './TermService';
import TitleDivider from '../shared/TitleDivider';
import CommunicationsCheck from './CommunicationsCheck';
import { INITIAL_FORM_DATA } from './formSchema';

const AboutYouStep = ({ onBack, onNext }) => {
  const [termPolicyOpen, setTermPolicyOpen] = useState(false);
  const { setTermServiceOpen, acceptTermService, getTermState } = useTerm();
  const { getTrustServiceState, trustServiceAgreement } = useTrustService();
  const { getProposalState } = useProposal();
  const { setReset } = useReset();
  const { createPerson, getPersonByDocument, getParticipantState, setParticipant } = useParticipant();
  const { AboutYouStepValidationSchema } = useValidator();
  const { addMsgWarning, addMsgDanger } = useAlert();

  useEffect(() => {
    if (getProposalState?.sequencial || getProposalState?.url || getProposalState?.id) setReset();
  }, []);

  const { setValues, ...formik } = useFormik({
    initialValues: INITIAL_FORM_DATA,
    validationSchema: AboutYouStepValidationSchema,
    validateOnBlur: true,
    onSubmit: async (formData) => {
      const { document, name, email, useSocialName, phone } = formData;

      if (name?.length <= MAX_CHARACTERS_ABOUT) {
        addMsgWarning(MESSAGE_ABOUT_CHARACTERS);
        return;
      }

      const payloadAccept = { email, name, phone };

      const acceptTermResponse = await acceptTermService(payloadAccept);

      if (acceptTermResponse.meta.requestStatus === FETCH_STATUS.fulfilled) {
        const { success } = acceptTermResponse.payload;

        if (success) {
          const payloadPerson = {
            document,
            name,
            email,
            phone,
            isSocialName: useSocialName,
          };

          const createPersonResponse = await createPerson(payloadPerson);

          if (createPersonResponse.meta.requestStatus === FETCH_STATUS.fulfilled) onNext();
          else addMsgDanger(MESSAGE_PERSON_ERROR);
        }
      } else addMsgDanger(MESSAGE_TERMS_ERROR);
    },
  });

  const handleBlur = (formProperty) => {
    setParticipant({ [formProperty]: formik.values[formProperty] });
  };

  const handleDocument = async () => {
    setParticipant({
      document: formik.values.document,
    });

    const getPersonByDocumentResponse = await getPersonByDocument(formik.values.document);
    if (getPersonByDocumentResponse.meta.requestStatus === FETCH_STATUS.rejected) {
      const restrictedDocument = getPersonByDocumentResponse.payload.errors.code === RESTRICTDOCUMENTERRORCODE;

      if (restrictedDocument) addMsgDanger(MESSAGE_GET_PERSON_ERROR_RESTRICT);
      else addMsgDanger(MESSAGE_GET_PERSON_ERROR);
    }
  };

  const handleNameBlur = () => {
    /* 
      socialName must also be set to keep up to date with
      the currently typed name, and not only
      the socialName returned from the backend 
    */
    const newPerson = {
      socialName: getParticipantState.useSocialName ? formik.values.name : getParticipantState.socialName,
      name: formik.values.name,
    };
    setParticipant(newPerson);
  };

  const handleChangeRadio = (shouldUseSocialName) => {
    setParticipant({
      useSocialName: shouldUseSocialName,
      name: shouldUseSocialName ? getParticipantState.socialName : getParticipantState.personName,
    });
  };

  const setSocialNameAgreementAcceptance = (accepted) => {
    /*
      Must set name as well, or when user refuses the term, 
      the radio is selected as 'no' and the name is kept the social name
    */
    setParticipant({
      useSocialName: accepted,
      name: accepted ? getParticipantState.socialName : getParticipantState.personName,
    });
    setTermPolicyOpen(false);
  };

  const onAcceptServiceAgreement = () => {
    formik.setFieldValue('trustedServiceAgreement', true);
    trustServiceAgreement(true);
    setTermServiceOpen(false);
  };

  useEffect(() => {
    setValues({
      ...getTrustServiceState,
      ...getParticipantState,
      ...getTermState,
    });
  }, [
    getTrustServiceState,
    getParticipantState?.name,
    getParticipantState?.email,
    getParticipantState?.phone,
    getParticipantState?.useSocialName,
    getTermState,
  ]);

  const isXs = useMediaQuery((theme) => theme.breakpoints.down('xs'));

  useEffect(() => {
    if (formik.values.document?.length === CPF_SIZE) handleDocument();
  }, [formik.values.document]);

  return (
    <>
      <Box mt={2} minHeight={HEIGHT_STEP_CONTAINER}>
        <TitleDivider text={ABOUT_YOU.title} minWidth={100} />

        <Box display="flex" flexWrap="wrap">
          <Grid container spacing={3}>
            <Grid item xs={12} md={3} className="space-field-about-you">
              <Box mb={1}>
                <CPFInput
                  data-testid="input-email-introductoryStep"
                  id="document"
                  title="CPF"
                  label="CPF"
                  {...formik.getFieldProps('document')}
                  error={formik.touched.document && !!formik.errors.document}
                  helperText={formik.touched.document && formik.errors.document}
                  fullWidth
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6} className="space-field-about-you">
              <FormControl fullWidth>
                <FormLabel>
                  <Typography variant="caption" color="textPrimary">
                    Deseja utilizar nome social?
                  </Typography>
                </FormLabel>
                <RadioGroup
                  row
                  id="useSocialName"
                  data-testid="radio-socialname-introductoryStep"
                  {...formik.getFieldProps('useSocialName')}
                >
                  <FormControlLabel
                    value
                    label="Sim"
                    title="Sim"
                    control={
                      <Radio
                        color="primary"
                        id="residencial-site-nome-social-radio"
                        onClick={() => {
                          setTermPolicyOpen(true);
                          handleChangeRadio(true);
                        }}
                        inputProps={{
                          'data-testid': 'input-socialName-accept-introductoryStep',
                        }}
                      />
                    }
                  />
                  <FormControlLabel
                    value={false}
                    label="Não"
                    title="Não"
                    control={
                      <Radio
                        color="primary"
                        id="residencial-site-nome-social-radio"
                        onClick={() => handleChangeRadio(false)}
                        inputProps={{
                          'data-testid': 'input-socialName-deny-introductoryStep',
                        }}
                      />
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid
              item
              xs={12}
              md={4}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              className="space-field-about-you"
            >
              <Box mb={1}>
                <TextField
                  id="name"
                  color="secondary"
                  disabled={!formik.values.useSocialName}
                  label="Nome"
                  title="Nome"
                  fullWidth
                  {...formik.getFieldProps('name')}
                  error={formik.touched.name && !!formik.errors.name}
                  helperText={formik.touched.name && formik.errors.name}
                  inputProps={{
                    maxLength: 150,
                    'data-testid': 'input-name-introductoryStep',
                    onBlur: handleNameBlur,
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={4} className="space-field-about-you">
              <TextField
                id="email"
                color="secondary"
                label="E-mail"
                title="E-mail"
                {...formik.getFieldProps('email')}
                fullWidth
                error={formik.touched.email && !!formik.errors.email}
                helperText={formik.touched.email && formik.errors.email}
                inputProps={{
                  maxLength: 150,
                  onBlur: () => {
                    handleBlur('email');
                  },
                  'data-testid': 'input-email-introductoryStep',
                }}
              />
            </Grid>

            <Grid item xs={12} md={3} className="space-field-about-you">
              <PhoneInput
                id="phone"
                color="secondary"
                title="Telefone"
                label="Telefone"
                {...formik.getFieldProps('phone')}
                error={formik.touched.phone && !!formik.errors.phone}
                helperText={formik.touched.phone && formik.errors.phone}
                fullWidth
                inputProps={{
                  onBlur: () => {
                    handleBlur('phone');
                  },
                  'data-testid': 'input-phone-introductoryStep',
                }}
              />
            </Grid>
          </Grid>
        </Box>

        <Box className="terms-container">
          <Grid container>
            <Grid item xs={12} sm={4}>
              <Box id="residencial-site-termos-servico-checkbox">
                <TermButton
                  formik={formik}
                  id="trustedService"
                  text="termos de serviço"
                  setShowTerm={setTermServiceOpen}
                  dataTestid="checkbox-service-introductoryStep"
                  testButtonid="button-service-introductoryStep"
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={8}>
              <Box>
                <CommunicationsCheck parentFormik={formik} />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <ButtonsControl
        dataTestid="button-forward-introductoryStep"
        onBack={onBack}
        onNext={formik.submitForm}
        disabled={!isEmpty(formik.errors)}
        isFirstStep
        gridBack={isXs ? 4 : 3}
        gridForward={isXs ? 4 : 3}
      />

      {termPolicyOpen && (
        <TermPolicy
          open={termPolicyOpen}
          setOpen={setTermPolicyOpen}
          onAccept={() => setSocialNameAgreementAcceptance(true)}
          onClose={() => setSocialNameAgreementAcceptance(false)}
        />
      )}

      {getTermState.openTerm && (
        <TermService open={getTermState.openTerm} setOpen={setTermServiceOpen} onAccept={onAcceptServiceAgreement} />
      )}
    </>
  );
};

export default AboutYouStep;

AboutYouStep.propTypes = {
  onBack: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
};
